/**
 * P-5 データ締切設定 (`/settings/data-closing-deadlines`、D-901)。
 * 設計: docs/design/v05-board-meeting-pdca-detail.md §7-5。
 * ⚠️ 3列しかないため表にせず、カード縦積みで統一する (320/375px 縮退の考慮が不要になる)。
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore, hasTenantWidePermission } from '@/stores/auth';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';

type Deadline = { data_type: string; days_after_month_end: number; label: string; is_default: boolean; updated_at: string | null };

const DATA_TYPE_LABEL: Record<string, string> = {
  sales: '売上', delivery_sales: 'デリバリー売上', delivery_details: '納品明細', labor: '人件費', opex: '諸経費',
};

export function DataClosingDeadlinesSettingsPage() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = hasTenantWidePermission(user?.effective_permissions, 'tenant_config', 'manage');
  const [editTarget, setEditTarget] = useState<Deadline | null>(null);
  const [days, setDays] = useState('');
  const [label, setLabel] = useState('');

  const { data, isLoading, isError } = useQuery<{ deadlines: Deadline[] }>({
    queryKey: ['data-closing-deadlines'],
    queryFn: () => apiFetch('/data-closing-deadlines'),
  });

  const saveMut = useMutation({
    mutationFn: () => apiFetch(`/data-closing-deadlines/${editTarget!.data_type}`, {
      method: 'PUT', body: JSON.stringify({ days_after_month_end: Number(days), label }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['data-closing-deadlines'] }); setEditTarget(null); toast.success('更新しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });

  const openEdit = (d: Deadline) => { setEditTarget(d); setDays(String(d.days_after_month_end)); setLabel(d.label); };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-page-title text-text-main">データ締切</h1>
        <p className="text-sm text-text-sub mt-1">
          データ種別ごとに「月末から何日後に確定とみなすか」を設定します。この設定は経営会議ブリーフのデータ充足判定に使われます。
        </p>
      </div>

      {isLoading ? (
        <p className="text-sm text-text-sub">読み込んでいます…</p>
      ) : isError ? (
        <p role="alert" className="text-sm text-alert-text">締切設定の取得に失敗しました。</p>
      ) : (
        <ul className="space-y-2">
          {(data?.deadlines ?? []).map((d) => (
            <li key={d.data_type} className="border border-surface-border rounded-lg bg-white p-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-text-body">{DATA_TYPE_LABEL[d.data_type] ?? d.data_type}</p>
                <p className="text-sm text-text-sub">月末から {d.days_after_month_end} 日後 — {d.label}</p>
                {d.is_default && <p className="text-xs text-text-sub">既定値を使用中</p>}
              </div>
              {canManage && <Button variant="secondary" size="sm" onClick={() => openEdit(d)}>編集</Button>}
            </li>
          ))}
        </ul>
      )}

      <Modal open={!!editTarget} title="締切を編集" onClose={() => setEditTarget(null)} size="sm"
        footer={editTarget && (
          <>
            <Button variant="secondary" onClick={() => setEditTarget(null)}>キャンセル</Button>
            <Button variant="primary" loading={saveMut.isPending} disabled={label.trim() === '' || days === ''}
              onClick={() => saveMut.mutate()}>保存</Button>
          </>
        )}>
        {editTarget && (
          <div className="space-y-3">
            <p className="text-sm text-text-body">{DATA_TYPE_LABEL[editTarget.data_type] ?? editTarget.data_type}</p>
            <div>
              <label className="block text-xs text-text-sub mb-1" htmlFor="dcd-days">月末から何日後</label>
              <input id="dcd-days" type="number" min={0} max={90} value={days} onChange={(e) => setDays(e.target.value)}
                className="w-full min-h-[44px] px-3 rounded border border-surface-border text-sm appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" />
            </div>
            <div>
              <label className="block text-xs text-text-sub mb-1" htmlFor="dcd-label">表示ラベル</label>
              <input id="dcd-label" type="text" maxLength={100} value={label} onChange={(e) => setLabel(e.target.value)}
                className="w-full min-h-[44px] px-3 rounded border border-surface-border text-sm" />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
